/**
 * One-shot signal that a fresh interactive login (password / OTP / biometric / 2FA) just succeeded
 * on a path that mounts the LoggedIn page. Each independent consumer reads it once per login.
 *
 * Marked at the post-login route decisions, and only when postLoginRoute is LOGGED_IN — a mid-flow
 * re-login (session expiry, recoverable decline) routes straight back into the flow and must not
 * leave a signal a later LoggedIn mount could consume for. The SIGN_UP / ADD_MOBILE
 * profile-completion detours converge on the same postLoginRoute afterwards, so their marks are
 * consumed when LoggedIn eventually mounts.
 *
 * Deliberately plain module state rather than redux: the auth slice is persisted to
 * sessionStorage, so any redux flag needs arming/disarming/consuming cases for every path that
 * could leave it stale (failed logins, abandoned 2FA, password reset, reloads, logout). Module
 * state can't be resurrected by persistence — a reload simply drops the signal and the consumers
 * defer to the next login — and it is only ever set at explicit success call sites, so there are
 * no false positives to repair. Never touched during SSR (set in user-event handlers, consumed in
 * a client effect).
 *
 * Multiple consumers: the remember-me mint and the login-time "Triggered Experiment" exposure for
 * the remember-me experiment (see state/experiments/hooks.ts) each read the signal once per login.
 * A single read-once boolean cannot serve independent once-per-login consumers (the first
 * reader clears it before the second runs), so the mark is tracked as a monotonically increasing
 * generation and each consumer remembers the last generation it has already consumed. All
 * consumers therefore fire exactly once per fresh interactive login, in any mount order, without
 * clobbering each other.
 */
import { markUsFebConsentInteractiveLogin } from '../one-offer-page/consentLoginSignal'

export type InteractiveLoginConsumer = 'remember-me-mint' | 'remember-me-exposure'

let loginGeneration = 0

const lastConsumedGeneration: Record<InteractiveLoginConsumer, number> = {
  'remember-me-mint': 0,
  'remember-me-exposure': 0,
}

export const markInteractiveLogin = (): void => {
  loginGeneration += 1
  // Every remember-me-eligible interactive login also counts for the US FEB consent POST
  // (CASHDAP-3450). The consent signal is a superset: some login completions mark it directly
  // without being remember-me mint sites — see flow/one-offer-page/consentLoginSignal.ts.
  markUsFebConsentInteractiveLogin()
}

/**
 * Returns whether a fresh interactive login is pending for the given consumer (i.e. a login has been
 * marked since this consumer last consumed it) WITHOUT recording a consumption. Use this to decide
 * whether to keep waiting before the consumer's work has actually happened, then call
 * `consumeInteractiveLoginSignal` only at the moment the work fires. Because the signal lives in
 * module state (not a component ref), peeking lets a page that unmounts mid-flow — e.g. a LoggedIn
 * detour to ADD_MOBILE before checkout starts — preserve the pending signal for the next mount.
 */
export const peekInteractiveLoginSignal = (consumer: InteractiveLoginConsumer = 'remember-me-mint'): boolean => {
  return loginGeneration > lastConsumedGeneration[consumer]
}

/**
 * Returns whether a fresh interactive login is pending for the given consumer (i.e. a login has been
 * marked since this consumer last consumed it), and records the consumption so a later call returns
 * false until the next login is marked. Defaults to the original `remember-me-mint` consumer so
 * pre-existing no-arg callers keep their single-consumer read-once contract.
 */
export const consumeInteractiveLoginSignal = (consumer: InteractiveLoginConsumer = 'remember-me-mint'): boolean => {
  if (loginGeneration > lastConsumedGeneration[consumer]) {
    lastConsumedGeneration[consumer] = loginGeneration
    return true
  }
  return false
}
